import stylesheet from './searchspring-recommendations.component.scss?as=stylesheet';
import {html, nothing, LitElement} from 'lit';
import GTM from 'extensions/gtm';
import RecommendationsService from 'custom-extensions/searchspring-recommendations/services/recommendations-service';
import BeaconService from 'custom-extensions/searchspring-recommendations/services/beacon-service';
import 'custom-extensions/ui-components/x-product-card';
import 'custom-extensions/ui-components/x-splide';

export default class SearchspringRecommendations extends LitElement {

	static get properties () {
		return {
			_recommendations: {
				type: Array
			},
			limits: {
				type: Number
			},
			list: {
				type: String
			},
			slideOptions: {
				attribute: 'carousel-options',
				type: Object
			},
			tags: {
				type: String
			},
			title: {
				type: String
			},
			type: {
				type: String
			}
		};
	};

	static get styles () {
		return [stylesheet];
	}

	get listSlug () {
		return this.slugify(this.list);
	}

	constructor ()
	{
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);
		this._recommendations = [];
		this.list = 'Product Recommendations';
		this.slideOptions = [];
		this.tags = 'no-results';
		this.title = '';
		this.type = '';
		this.limits = 20;

		this.addEventListener('product-card-click', (e) => {
			this.sendClick(e.detail);
		});
	}

	async connectedCallback () {
		super.connectedCallback();

		this.addEventListener('afterRecommendations', () => {
			this.sendImpressions();
		});

		const recommendationsService = new RecommendationsService();
		this._recommendations = await recommendationsService.getRecommendations(this.tags, this.list, this.limits);
	}

	sendImpressions () {
		if (!this?._recommendations?.length) {
			return;
		}
		const gtmList = this?.list;
		this.gtm.searchSendImpressions(this.gtm.slugify(gtmList), gtmList, this?._recommendations);
		const renderProfile = new BeaconService();
		renderProfile.sendBeacon(this.tags, 'render', this?._recommendations);
	}

	initIntersectionObserver () {
		const element = this.shadowRoot.getElementById('js-' + this.listSlug + '-carousel') ?? false;
		if (element) {
			const options = {
				root: null,
				rootMargin: '0px',
				threshold: 0
			};
			let observer = new IntersectionObserver(this.intersected, options);

			observer.observe(element);
		}
	}

	intersected (entries, observer) {
		let visibleProducts = [];
		let products = [];

		entries.forEach((entry) => {
			const tags = entry.target.parentNode.host.getAttribute('tags');

			if (entry.isIntersecting) {
				visibleProducts = entry.target.querySelector('x-splide').shadowRoot.querySelectorAll('.splide__slide.is-visible');

				if (visibleProducts.length) {
					visibleProducts.forEach((product) => {
						let productCard = product.querySelector('slot').assignedElements({
							flatten: true,
							selector: 'x-product-card'
						})[0];

						const prodAttributesArray = [...productCard.attributes];
						let productCardAttributes = prodAttributesArray.reduce((productCardAttributes, attribute) => {
							productCardAttributes[attribute.name] = attribute.value;
							return productCardAttributes;
						}, {});
						productCardAttributes.id = productCardAttributes['product-id'];
						products.push(productCardAttributes);
					});

					const impressionProfile = new BeaconService();
					impressionProfile.sendBeacon(tags, 'impression', products);
				}

				// Only trigger on first intersection
				observer.unobserve(entry.target);
			}
		});
	}

	sendClick (data) {
		const renderProfile = new BeaconService();
		renderProfile.sendBeacon(data.item_list_id, 'click', [data]);
	}

	updated () {
		super.updated();

		this.dispatchEvent(new CustomEvent('afterRecommendations'));
		this.initIntersectionObserver();
	}

	renderProductResults () {
		const gtmData = {
			listId: this.gtm.slugify(this?.list),
			listName: this?.list
		};

		return this._recommendations?.map((product, index) => {
			const pos = index + 1;
			const shippingFlags = {
				isBopisOnly: product?.is_bopis_only,
				isDeliveryOnly: product?.is_delivery_only,
				isInStoreOnly:product?.is_in_store_only,
				isNotForSale: product?.is_not_for_sale
			};

			return html`
				<x-product-card
					brand="${product?.brand}"
					code="${product.code}"
					hover-src-desktop="${product?.images?.secondary?.desktop}"
					hover-src-mobile="${product?.images?.secondary?.mobile}"
					gtm-list="${gtmData?.listName}"
					gtm-list-id="${gtmData?.listId}"
					gtm-position="${pos}"
					image-src-desktop="${product?.images?.main?.desktop}"
					image-src-mobile="${product?.images?.main?.mobile}"
					name="${product.name}"
					original-price="${product?.customfields?.original_price}"
					price="${product.price}"
					product-id = "${product?.id}"
					shipping-flags = "${shippingFlags}"
					style-number = "${product?.sku}"
					url = "${product.url}"
				></x-product-card>
			`;
		});
	}

	render () {
		if (!this._recommendations?.length) {
			return nothing;
		}

		return html`
		<div id="js-${this.listSlug}-carousel" class="x-related-products">
			<div class="x-layout__item u-width-12">
				${this.title ? html`<h2 class="ss-recommendations__title">${this.title}</h2>` : nothing}
				<x-splide .type="${this.type}" .options="${this.slideOptions}">
					${this.renderProductResults()}
				</x-splide>
			</div>
		</div>
		`;
	}

	// Helpers
	htmlEntities (str) {
		// eslint-disable-next-line newline-per-chained-call
		return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	/**
	 * Slugify a string
	 * @param {String} text
	 * @returns {void}
	 */
	slugify (text) {
		return text.toLowerCase().replace(/[^\w ,-]+/g, '').replace(/ +/g, '-');
	};
};

window.customElements.define('x-ss-recommendations', SearchspringRecommendations);
